import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface Consents {
  consent_rgpd: boolean;
  consent_cgu: boolean;
  consent_droits_auteur: boolean;
}

interface Props {
  data: Consents;
  onChange: (field: keyof Consents, value: boolean) => void;
}

const RGPD_TEXT = `Smart-EFI collecte et traite vos données personnelles (nom, prénom, email, profession, pays, téléphone) dans le cadre de la fourniture de ses services de montage et vérification d'états financiers. Ces données sont traitées sur la base de votre consentement et de l'exécution du contrat. Elles sont conservées pendant la durée de votre abonnement plus 5 ans. Vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition. Pour exercer ces droits, contactez notre DPO à dpo@smart-efi.com. Vous pouvez retirer votre consentement à tout moment, sans que cela ne remette en cause la licéité du traitement antérieur.`;

const CGU_TEXT = `En utilisant Smart-EFI, vous acceptez d'utiliser l'application conformément à sa destination, à savoir la préparation et la vérification d'états financiers. Il est interdit de tenter de contourner les mesures de sécurité, de procéder à de la rétro-ingénierie, ou de partager vos identifiants de connexion. Smart-EFI se réserve le droit de suspendre tout compte en violation de ces conditions. La responsabilité de Smart-EFI est limitée aux montants effectivement payés dans les 12 mois précédant le litige. Ces conditions sont régies par le droit applicable dans votre pays de résidence.`;

const DROITS_TEXT = `Les modèles d'états financiers, algorithmes de mapping, logique de calcul et interfaces de Smart-EFI sont protégés par le droit d'auteur et constituent la propriété intellectuelle exclusive de Smart-EFI et de ses concédants. Toute reproduction, modification, distribution ou utilisation à des fins commerciales sans autorisation expresse est strictement interdite. Les données financières que vous importez restent votre propriété. Smart-EFI ne revendique aucun droit sur vos données métier.`;

interface ConsentItemProps {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  text: string;
  required?: boolean;
}

function ConsentItem({ id, checked, onChange, title, text, required }: ConsentItemProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn(
      "rounded-lg border p-4 space-y-3 transition-colors",
      checked ? "border-primary/40 bg-primary/3" : "border-border"
    )}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(v) => onChange(!!v)}
          className="mt-0.5 shrink-0"
        />
        <Label htmlFor={id} className="cursor-pointer leading-snug">
          <span className="font-medium">{title}</span>
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-primary hover:underline ml-7"
      >
        {expanded ? "Réduire ▲" : "Lire le texte complet ▼"}
      </button>

      {expanded && (
        <ScrollArea className="h-32 ml-7">
          <p className="text-xs text-muted-foreground leading-relaxed pr-3">{text}</p>
        </ScrollArea>
      )}
    </div>
  );
}

export function StepConsents({ data, onChange }: Props) {
  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-lg p-3">
        <p className="text-sm text-muted-foreground">
          Veuillez lire chaque section et cocher les cases correspondantes.
          Les trois consentements sont obligatoires pour utiliser Smart-EFI.
        </p>
      </div>

      <ConsentItem
        id="consent_rgpd"
        checked={data.consent_rgpd}
        onChange={(v) => onChange("consent_rgpd", v)}
        title="Protection des données personnelles (RGPD)"
        text={RGPD_TEXT}
        required
      />

      <ConsentItem
        id="consent_cgu"
        checked={data.consent_cgu}
        onChange={(v) => onChange("consent_cgu", v)}
        title="Conditions Générales d'Utilisation"
        text={CGU_TEXT}
        required
      />

      <ConsentItem
        id="consent_droits_auteur"
        checked={data.consent_droits_auteur}
        onChange={(v) => onChange("consent_droits_auteur", v)}
        title="Droits d'auteur et Propriété intellectuelle"
        text={DROITS_TEXT}
        required
      />
    </div>
  );
}
